import { token } from '@atlaskit/tokens';
import FieldNameContainer from '@databases/field-name/FieldNameContainer';
import { FieldNameInlineContainer } from '@databases/field-name/FieldNameWithActions';
import { InlineEditWrapperContainer } from '@databases/inline-edit/InlineEditWrapper';
import { useDatabaseSelectionSnapshot } from '@databases/ping-registry';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { TABLE_BORDER_RADIUS } from '@databases/styles';
import { shouldShowField } from '@databases/types/database/view';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { gridTable, newTableBorderWidth, subtleBorder, SubtleTable } from '../styled';

import DatabaseEntryRow from './DatabaseEntryRow';

export const Container = styled.div<{
  isEmbedEntryView?: boolean;
  isNativeEmbedsV2?: boolean;
}>`
  min-height: 0;
  overflow: ${(props) => (props.isEmbedEntryView ? 'visible' : 'auto')};
  box-sizing: border-box;
  flex-grow: 1;
  margin-bottom: ${token('space.100', '8px')};

  ${(props) =>
    props.isNativeEmbedsV2 &&
    `
    padding-bottom: ${token('space.200', '16px')};
    margin-bottom: 0;
  `}
`;

export const Table = styled(SubtleTable)<{
  fieldNameWidth?: number;
  isNativeEmbedsV2?: boolean;
}>`
  ${gridTable}
  grid-template-columns: ${(props) => `${props.fieldNameWidth ?? 260}px`} 1fr;
  overflow-wrap: anywhere;

  ${(props) =>
    props.isNativeEmbedsV2 &&
    `
    &, > * > tr > *, > * > tr > *:last-child {
      border: none !important;
    }

    > * > tr:first-child > * {
      padding-top: ${token('space.050', '4px')};
    }

    > * > tr:last-child > * {
      padding-bottom: ${token('space.050', '4px')};
    }
  `}

  ${(props) =>
    props.isNativeEmbedsV2 &&
    `
    grid-template-columns: ${props.fieldNameWidth ?? 140}px 1fr;
    column-gap: ${token('space.100', '8px')};

    > * > tr > * {
      min-height: 32px;
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > :first-child {
      top: 15px;
    }

    > * > tr > th ${FieldNameInlineContainer} > ${FieldNameContainer} > ${InlineEditWrapperContainer}:nth-child(2) {
      height: auto;
      justify-content: flex-start;
      top: 15px;
    }
  `}

  > * > tr > * {
    display: flex;
    flex-direction: column;
    min-height: 42px;
    min-width: 0;
    padding: 0;
    box-sizing: border-box;

    &:last-child {
      border-right: ${subtleBorder};
      border-width: ${newTableBorderWidth}px;
    }

    > ${InlineEditWrapperContainer} {
      flex-grow: 1;

      ${FieldNameInlineContainer} {
        box-sizing: border-box;
        padding: ${token('space.100', '8px')};
        font: ${token('font.heading.xxsmall')};
      }
    }
  }

  > * > tr {
    &:first-child {
      th {
        border-start-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-start-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
    &:last-child {
      th {
        border-end-start-radius: ${TABLE_BORDER_RADIUS};
      }
      td {
        border-end-end-radius: ${TABLE_BORDER_RADIUS};
      }
    }
  }
`;

export interface DatabaseEntryProps {
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  fieldNameWidth?: number;
  /** If true, focus the first value. */
  autoFocus?: boolean;
  isEmbedEntryView?: boolean;
  /** If true, displays greyed out / color.disabled for database values */
  showDisabledValue?: boolean;
  /** If true, applies native embeds v2 layout: removes borders, reduces field
   *  gap, adds block padding, and aligns field names with the toolbar title. */
  isNativeEmbedsV2?: boolean;
  /** If true, omits the first visible field row from the body. */
  hideFirstVisibleField?: boolean;
  emptyValueLabel?: string;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntry: FunctionComponent<DatabaseEntryProps> = ({
  contentId,
  entryId,
  isContentEditable = false,
  fieldNameWidth,
  autoFocus,
  isEmbedEntryView,
  showDisabledValue = false,
  isNativeEmbedsV2 = false,
  hideFirstVisibleField = false,
  emptyValueLabel
}) => {
  const [allFieldIds, operations, isLocked] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: true
      });
      return [
        databaseDraft.fields,
        databaseDraft.operations,
        databaseDraft.meta.isLocked
      ] as const;
    }
  );

  const { view } = useView();
  const fieldVisibilityMap = view.settings.fields;

  const isHideFieldsEnabled = useMemo(() => {
    const isMauiEnabled = getExperimentValue('mauiExperiment', 'isEnabled', {
      fireExperimentExposure: false
    });
    return (
      isMauiEnabled &&
      getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
        fireExperimentExposure: true
      })
    );
  }, []);

  const fieldIds = useMemo(() => {
    if (!isHideFieldsEnabled) {
      return hideFirstVisibleField ? allFieldIds.slice(1) : allFieldIds;
    }
    const visibleFieldIds = allFieldIds.filter((fieldId) =>
      shouldShowField(fieldVisibilityMap, fieldId)
    );
    return hideFirstVisibleField ? visibleFieldIds.slice(1) : visibleFieldIds;
  }, [allFieldIds, fieldVisibilityMap, hideFirstVisibleField, isHideFieldsEnabled]);

  const canEditContent = isContentEditable && operations.updateEntries;
  const canEditFields =
    isContentEditable && operations.updateMetadata && !isLocked && !isEmbedEntryView;

  const editingIds = useRef(new Set<string>());

  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );

  const [selectedPanelFieldId, setSelectedPanelFieldId] = useState<string | null>(null);

  const selectPanelField = useCallback((nextFieldId: string) => {
    setSelectedPanelFieldId(nextFieldId);
  }, []);

  const clearPanelSelection = useCallback(() => setSelectedPanelFieldId(null), []);

  // Keep panel and grid selections mutually exclusive.
  const hasGlobalSelection = useDatabaseSelectionSnapshot((reader) =>
    isSelectionsPerformanceEnabled ? reader.hasSelection() : false
  );
  useEffect(() => {
    if (hasGlobalSelection) {
      setSelectedPanelFieldId(null);
    }
  }, [hasGlobalSelection]);

  return (
    <Container isEmbedEntryView={isEmbedEntryView} isNativeEmbedsV2={isNativeEmbedsV2}>
      <Table
        data-testid={'database-entry-table'}
        fieldNameWidth={fieldNameWidth}
        isNativeEmbedsV2={isNativeEmbedsV2}
      >
        <tbody>
          {fieldIds.map((fieldId, i) => (
            <DatabaseEntryRow
              key={fieldId}
              contentId={contentId}
              entryId={entryId}
              fieldId={fieldId}
              isContentEditable={isContentEditable}
              canEditContent={canEditContent}
              canEditFields={canEditFields}
              editingIds={editingIds}
              autoFocus={autoFocus && i === 0}
              showDisabledValue={showDisabledValue}
              selectedPanelFieldId={selectedPanelFieldId}
              selectPanelField={selectPanelField}
              clearPanelSelection={clearPanelSelection}
              emptyValueLabel={emptyValueLabel}
            />
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default DatabaseEntry;
